import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

// ── Types ──────────────────────────────────────────────────────────────────
interface Contact {
  name: string;
  emails: string; // semicolon-separated
  _rowNumber: number;
}

interface Artist {
  id: number;
  name: string;
  b2b: boolean;
}

interface Venue {
  name?: string;
  city?: string;
  state?: string;
  country?: string;
}

interface Show {
  id: number;
  name: string | null;
  date: string;
  isFestival: boolean;
  link: string | null;
  venue: Venue;
  artists: Artist[];
}

interface Location {
  id: number;
  city: string | null;
  state: string;
  stateCode: string;
  label: string;
}

// A single selected send target: one artist + one chosen email address.
// (An artist with 3 emails can produce up to 3 of these.) date/venue are
// carried so they can fill {{date}}/{{venue}} template variables.
interface Recipient {
  key: string;
  artistName: string;
  showName: string;
  contactName: string;
  email: string;
  cc: string[];
  date: string;
  venue: string;      // "Venue Name, City"
  venueName: string;  // just "Venue Name"
  city: string;       // just "City"
}

// Recipients sharing the same primary email get merged into one send.
interface GroupedRecipient {
  email: string;
  cc: string[];
  contactName: string;
  artistNames: string[];
  showNames: string[];
  dates: string[];
  venues: string[];
  venueNames: string[];
  cities: string[];
}

interface Template {
  name: string;
  subject: string;
  body: string;
  _rowNumber: number;
}

interface SentLogEntry {
  to: string;
  subject: string;
  sentAt: string;
  ok: boolean;
  error?: string;
}

const DEFAULT_LOCATION_ID = 70; // New York City specifically
const DEFAULT_LOCATION_LABEL = 'New York City, NY';
const ANY_LOCATION = -1; // sentinel: search all locations, sorted by date
const DEFAULT_WINDOW_DAYS = 90;
const STAGGER_DELAY_MS = 45000; // 45s between staggered sends

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const normalize = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
function formatDateBadge(dateStr: string) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return { month: MONTH_ABBR[(m || 1) - 1], day: d, year: y };
}
function formatDateLong(dateStr: string) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d);
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
// Short M/D form, e.g. "6/26" - for casual references like "your set on {{shortdate}}".
function formatDateShort(dateStr: string) {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${m}/${d}`;
}

// Generic mailbox local-parts that aren't a person's name. If the part before
// the @ matches one of these, {{name}} falls back to "[artist]'s team"
// instead of e.g. "Good Afternoon info,".
const GENERIC_LOCALPARTS = new Set([
  'info', 'booking', 'bookings', 'management', 'mgmt', 'contact', 'contacts',
  'hello', 'hi', 'team', 'office', 'admin', 'mail', 'email', 'press',
  'general', 'inquiries', 'enquiries', 'label', 'music', 'artists', 'artist',
  'agency', 'talent', 'contato', 'reservations', 'support', 'help', 'sales',
]);

// Derives a greeting name from an email's local-part. "dave@x.com" -> "Dave".
// Generic mailboxes (info@, bookings@, etc.) -> "<artist>'s team". Single
// letters or anything with digits also fall back, since they're not real names.
function deriveGreetingName(email: string, artistLabel: string): string {
  const localPart = (email.split('@')[0] || '').toLowerCase().trim();
  const teamFallback = artistLabel ? `${artistLabel}'s team` : 'there';

  if (!localPart) return teamFallback;
  if (GENERIC_LOCALPARTS.has(localPart)) return teamFallback;
  if (localPart.length <= 1) return teamFallback;      // "a@..." isn't a name
  if (/\d/.test(localPart)) return teamFallback;        // has digits, not a name

  // Take the first token if it's a "first.last" / "first_last" style address.
  const first = localPart.split(/[._-]/)[0];
  if (first.length <= 1) return teamFallback;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function groupRecipientsByEmail(recipients: Recipient[]): GroupedRecipient[] {
  const map = new Map<string, GroupedRecipient>();
  for (const r of recipients) {
    const key = r.email.toLowerCase();
    const existing = map.get(key);
    if (existing) {
      if (!existing.artistNames.includes(r.artistName)) existing.artistNames.push(r.artistName);
      if (!existing.showNames.includes(r.showName)) existing.showNames.push(r.showName);
      if (r.date && !existing.dates.includes(r.date)) existing.dates.push(r.date);
      if (r.venue && !existing.venues.includes(r.venue)) existing.venues.push(r.venue);
      if (r.venueName && !existing.venueNames.includes(r.venueName)) existing.venueNames.push(r.venueName);
      if (r.city && !existing.cities.includes(r.city)) existing.cities.push(r.city);
      for (const c of r.cc) if (!existing.cc.includes(c)) existing.cc.push(c);
    } else {
      map.set(key, {
        email: r.email,
        cc: [...r.cc],
        contactName: r.contactName,
        artistNames: [r.artistName],
        showNames: [r.showName],
        dates: r.date ? [r.date] : [],
        venues: r.venue ? [r.venue] : [],
        venueNames: r.venueName ? [r.venueName] : [],
        cities: r.city ? [r.city] : [],
      });
    }
  }
  return Array.from(map.values());
}

// Text colors kept at #9a9a9a or lighter for anything meant to be read.
// Base font sizes bumped up from the cramped earlier version.
const css = `
  .divider { width: 32px; height: 2px; background: #4db0f2; margin: 12px 0 32px; }

  .admin-input, .admin-select, .admin-textarea {
    padding: 11px 14px;
    background: #0a0a0a;
    color: #e8e8e8;
    border: 1px solid #2a2a2a;
    border-radius: 3px;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.0rem;
    letter-spacing: 0.02em;
    outline: none;
    transition: border-color 0.2s ease;
  }
  .admin-input:focus, .admin-select:focus, .admin-textarea:focus { border-color: #4db0f2; }
  .admin-input::placeholder, .admin-textarea::placeholder { color: #666; }
  .admin-textarea { width: 100%; resize: vertical; font-family: 'Montserrat', monospace; line-height: 1.6; font-size: 0.98rem; min-height: 200px; }

  /* Reliable date picker: force dark scheme, make the icon visible, and make
     the whole field open the calendar (not just the tiny icon). */
  .admin-input[type="date"] { color-scheme: dark; cursor: pointer; }
  .admin-input[type="date"]::-webkit-calendar-picker-indicator {
    filter: invert(1) brightness(1.8);
    cursor: pointer;
    opacity: 1;
  }

  .btn {
    padding: 10px 20px;
    border-radius: 3px;
    border: 1px solid #2a2a2a;
    background: #0d0d0d;
    color: #b8b8b8;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
  }
  .btn:hover { border-color: #4db0f2; color: #fff; }
  .btn:disabled { opacity: 0.35; cursor: not-allowed; }
  .btn-confirm { border-color: #4db0f2; color: #a6d7f8; background: rgba(77,176,242,0.1); }
  .btn-confirm:hover { border-color: #fff; color: #fff; }
  .btn-danger { border-color: #5a2020; color: #d08080; }
  .btn-danger:hover { border-color: #ff4444; color: #ff8888; }
  .btn-toggle-on { border-color: #4db0f2; color: #fff; background: #4db0f2; }
  .btn-toggle-on:hover { border-color: #fff; }
  .btn-tab { border: none; border-bottom: 2px solid transparent; border-radius: 0; background: transparent; padding: 8px 4px; margin-right: 16px; }
  .btn-tab-active { border-bottom-color: #4db0f2; color: #fff; }

  .search-bar {
    display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end;
    padding: 20px; border: 1px solid #2a2a2a; border-radius: 4px;
    background: #0d0d0d; margin-bottom: 20px;
  }
  .search-bar label {
    font-family: 'Montserrat', sans-serif; font-size: 0.68rem; color: #9a9a9a;
    letter-spacing: 0.12em; text-transform: uppercase; display: block; margin-bottom: 6px;
  }
  .search-field { display: flex; flex-direction: column; }

  .session-toggle {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 20px; cursor: pointer; padding: 12px 16px;
    border: 1px solid #2a2a2a; border-radius: 4px; background: #0d0d0d;
  }
  .session-toggle span { font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; color: #b8b8b8; }
  .session-log-item {
    display: flex; justify-content: space-between; gap: 12px; padding: 10px 16px;
    border-bottom: 1px solid #1a1a1a; font-size: 0.8rem; color: #c8c8c8;
  }
  .session-log-item .ok { color: #7ed9a0; }
  .session-log-item .fail { color: #d08080; }

  .show-card {
    display: flex; border: 1px solid #2a2a2a; border-radius: 4px; background: #0d0d0d;
    margin-bottom: 14px; overflow: hidden;
  }
  .date-badge {
    flex-shrink: 0; width: 84px; padding: 16px 10px;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    background: #0a0a0a; border-right: 1px solid #2a2a2a;
  }
  .date-badge .month { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em; color: #4db0f2; }
  .date-badge .day { font-size: 2.1rem; font-weight: 700; color: #fff; line-height: 1.1; font-family: 'Azonix', sans-serif; }
  .date-badge .year { font-size: 0.68rem; color: #9a9a9a; letter-spacing: 0.06em; }

  .show-body { flex: 1; min-width: 0; }
  .show-header { padding: 16px 18px 12px; border-bottom: 1px solid #1e1e1e; }
  .show-title-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 5px; }
  .show-title { font-family: 'Montserrat', sans-serif; font-size: 1.15rem; font-weight: 700; color: #fff; }
  .show-venue { font-family: 'Montserrat', sans-serif; font-size: 0.85rem; color: #b8b8b8; }
  .festival-tag {
    font-size: 0.62rem; letter-spacing: 0.12em; text-transform: uppercase;
    color: #0a0a0a; background: #4db0f2; border-radius: 2px; padding: 3px 8px; font-weight: 700;
  }

  .artist-row {
    display: flex; align-items: center; gap: 12px; padding: 12px 18px;
    border-bottom: 1px solid #161616; flex-wrap: wrap;
  }
  .artist-row:last-child { border-bottom: none; }
  .artist-name { font-family: 'Montserrat', sans-serif; font-size: 1rem; color: #e4e4e4; flex: 1; min-width: 100px; }

  .contact-pill {
    font-size: 0.66rem; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 600;
    padding: 5px 11px; border-radius: 20px; white-space: nowrap; cursor: pointer;
    border: 1px solid #2a5a3a; color: #8ee9ac; background: rgba(80,200,120,0.1);
  }
  .contact-pill:hover { border-color: #4db0f2; }
  .no-contact-pill {
    font-size: 0.66rem; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 600;
    padding: 5px 11px; border-radius: 20px; white-space: nowrap; cursor: pointer;
    border: 1px dashed #5a5a5a; color: #b0b0b0; background: transparent;
  }
  .no-contact-pill:hover { border-color: #4db0f2; color: #a6d7f8; }

  .add-contact-inline { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

  .email-picker {
    width: 100%; margin-top: 8px; padding: 10px 14px; background: #0a0a0a;
    border: 1px solid #222; border-radius: 4px;
  }
  .email-option {
    display: flex; align-items: center; gap: 10px; padding: 6px 0; font-size: 0.82rem; color: #d0d0d0;
  }
  .email-option .addr { flex: 1; }
  .email-option .cc-toggle {
    font-size: 0.62rem; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 2px 8px; border: 1px solid #333; border-radius: 3px; color: #9a9a9a; cursor: pointer; background: #0d0d0d;
  }
  .email-option .cc-toggle.on { border-color: #bfa054; color: #e1c679; }

  .compose-panel {
    position: fixed; bottom: 0; right: 0; left: 0;
    background: #0a0a0a; border-top: 1px solid #4db0f2;
    padding: 20px 48px; z-index: 150;
    box-shadow: 0 -8px 30px rgba(0,0,0,0.7);
    max-height: 80vh; overflow-y: auto;
  }
  .compose-row { display: flex; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; align-items: center; }
  .var-btn {
    font-size: 0.66rem; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 5px 9px; border: 1px solid #333; border-radius: 3px; color: #b0b0b0;
    cursor: pointer; background: #0d0d0d;
  }
  .var-btn:hover { border-color: #4db0f2; color: #a6d7f8; }
  .link-btn {
    font-size: 0.66rem; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 5px 9px; border: 1px solid #bfa054; border-radius: 3px; color: #e1c679;
    cursor: pointer; background: #0d0d0d;
  }
  .link-btn:hover { border-color: #e1c679; color: #fff; }

  .preview-box {
    background: #fff; color: #111; border-radius: 4px; padding: 20px;
    font-family: Arial, sans-serif; white-space: pre-wrap; min-height: 200px; font-size: 1.02rem;
  }
  /* Force dark text on everything inside the preview - the pasted email HTML
     otherwise inherits the page's near-white text color, making it invisible
     on the white preview background (only visible when highlighted). */
  .preview-box, .preview-box * { color: #111 !important; }
  .preview-box a { color: #4db0f2 !important; }
  .group-note { font-size: 0.72rem; color: #9a9a9a; margin: 2px 0 10px; }

  .toast {
    position: fixed; bottom: 32px; right: 32px; background: #0d0d0d;
    border: 1px solid #4db0f2; border-radius: 3px; padding: 13px 22px;
    font-family: 'Montserrat', sans-serif; font-size: 0.76rem; letter-spacing: 0.1em;
    color: #a6d7f8; text-transform: uppercase; z-index: 200;
  }
`;

const API = process.env.REACT_APP_API_BASE_URL;

// Brian's signature, hardcoded so it's always present on every machine with
// nothing to configure. Appended to the bottom of every send. Editable live
// in the compose panel if a one-off tweak is ever needed, but this is the
// default it always loads with.
const DEFAULT_SIGNATURE = `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-top:16px"><tbody><tr><td style="padding-right:24px;border-right:2px solid rgb(77,176,242);vertical-align:middle;text-align:center"><div style="font-family:Montserrat,Arial,sans-serif;font-size:16px;font-weight:bold;color:rgb(34,34,34);letter-spacing:0.06em">BDAN MEDIA</div><div style="font-family:Montserrat,Arial,sans-serif;font-size:8px;font-weight:600;letter-spacing:0.22em;color:rgb(77,176,242);text-transform:uppercase;margin-top:8px">Photo · Video · Multicam</div></td><td style="padding-left:24px;vertical-align:middle"><div style="font-family:Montserrat,Arial,sans-serif;font-size:13px;font-weight:600;color:rgb(34,34,34);letter-spacing:0.04em;margin-bottom:6px">Brian Daniels</div><div style="font-family:Montserrat,Arial,sans-serif;font-size:11px;color:rgb(102,102,102);letter-spacing:0.04em;margin-bottom:3px">973-986-6827</div><div style="font-family:Montserrat,Arial,sans-serif;font-size:11px;letter-spacing:0.04em"><a href="https://bdanmedia.com/" style="color:rgb(77,176,242)" target="_blank">bdanmedia.com</a></div></td></tr></tbody></table>`;

// Minimal CSV parser (handles quoted fields with commas/newlines) so we don't
// need to pull in an external dependency. Returns array of row-objects keyed
// by the header row. Expected headers for uploaded shows:
//   artist, venue, city, date, festival, b2b_with
function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { cur.push(field); field = ''; }
      else if (c === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; }
      else if (c === '\r') { /* ignore */ }
      else field += c;
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  if (rows.length === 0) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1)
    .filter((r) => r.some((cell) => cell.trim() !== ''))
    .map((r) => {
      const obj: Record<string, string> = {};
      header.forEach((h, i) => { obj[h] = (r[i] || '').trim(); });
      return obj;
    });
}

// Turns parsed CSV rows into the same Show[] shape EDMTrain search produces,
// so uploaded shows flow through the identical matching/compose/send pipeline.
// Rows sharing venue+city+date+festival collapse into one show card. A row's
// b2b_with artist becomes its own emailable Artist on that show.
function csvRowsToShows(rows: Record<string, string>[]): { shows: Show[]; skipped: number } {
  const showMap = new Map<string, Show>();
  let idCounter = -1; // negative ids so they never collide with EDMTrain's
  let artistIdCounter = -1;
  let skipped = 0;

  for (const row of rows) {
    const artistName = (row['artist'] || '').trim();
    if (!artistName) { skipped++; continue; }
    const venue = (row['venue'] || '').trim();
    const city = (row['city'] || '').trim();
    const date = (row['date'] || '').trim(); // expected YYYY-MM-DD
    const festival = (row['festival'] || '').trim();
    const b2bWith = (row['b2b_with'] || '').trim();

    const showKey = `${venue}|${city}|${date}|${festival}`.toLowerCase();
    let show = showMap.get(showKey);
    if (!show) {
      show = {
        id: idCounter--,
        name: festival || venue || artistName,
        date: date || '',
        isFestival: !!festival,
        link: null,
        venue: { name: venue, city },
        artists: [],
      };
      showMap.set(showKey, show);
    }

    // Add the main artist (avoid duplicating if the same name is already on this show)
    if (!show.artists.some((a) => a.name.toLowerCase() === artistName.toLowerCase())) {
      show.artists.push({ id: artistIdCounter--, name: artistName, b2b: !!b2bWith });
    }
    // b2b partner becomes its own emailable artist on the same show
    if (b2bWith && !show.artists.some((a) => a.name.toLowerCase() === b2bWith.toLowerCase())) {
      show.artists.push({ id: artistIdCounter--, name: b2bWith, b2b: true });
    }
  }

  const shows = Array.from(showMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  return { shows, skipped };
}

const OutreachPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  // Tracks which field the cursor was last in, so Insert buttons drop the
  // token into the subject or the body depending on where you were typing.
  const [lastFocused, setLastFocused] = useState<'subject' | 'body'>('body');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  // ── Search state ─────────────────────────────────────────────────────────
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState<number>(DEFAULT_LOCATION_ID);
  const [eventName, setEventName] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(addDaysISO(DEFAULT_WINDOW_DAYS));
  const [festivalsOnly, setFestivalsOnly] = useState(false);

  const [shows, setShows] = useState<Show[]>([]);
  const [uploadedMode, setUploadedMode] = useState(false);
  const [showCsvSample, setShowCsvSample] = useState(false);
  const [showSelectedList, setShowSelectedList] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [contactsByNorm, setContactsByNorm] = useState<Map<string, Contact>>(new Map());

  // ── Inline add-contact state ─────────────────────────────────────────────
  const [addingContactFor, setAddingContactFor] = useState<string | null>(null);
  const [newContactEmail, setNewContactEmail] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  // Appending an extra email to an artist who already has a contact.
  const [appendingFor, setAppendingFor] = useState<string | null>(null);
  const [appendEmail, setAppendEmail] = useState('');
  // Standalone "add any contact" modal (not tied to a show artist).
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  const [standaloneName, setStandaloneName] = useState('');
  const [standaloneEmail, setStandaloneEmail] = useState('');

  // ── Which artist rows have the email-picker expanded ─────────────────────
  const [expandedPicker, setExpandedPicker] = useState<Set<string>>(new Set());

  // ── Selection: keyed by `${artistName}::${email}` ────────────────────────
  const [selected, setSelected] = useState<Map<string, Recipient>>(new Map());

  // ── Compose ──────────────────────────────────────────────────────────────
  const [composing, setComposing] = useState(false);
  const [composeView, setComposeView] = useState<'write' | 'preview'>('write');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [signature, setSignature] = useState(DEFAULT_SIGNATURE);
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);
  const [sending, setSending] = useState(false);
  const [staggerSend, setStaggerSend] = useState(false);
  const [sendProgress, setSendProgress] = useState('');
  const [toast, setToast] = useState('');
  const [subjectOverrides, setSubjectOverrides] = useState<Map<string, string>>(new Map());
  const [customizingSubjects, setCustomizingSubjects] = useState(false);
  const [nameOverrides, setNameOverrides] = useState<Map<string, string>>(new Map());
  const [checkingNames, setCheckingNames] = useState(false);
  // ── Preview review flow ──────────────────────────────────────────────────
  const [previewIdx, setPreviewIdx] = useState(0);
  const [approvedEmails, setApprovedEmails] = useState<Set<string>>(new Set());
  const [bodyOverrides, setBodyOverrides] = useState<Map<string, string>>(new Map());
  const [editingThisOne, setEditingThisOne] = useState(false);

  // ── Templates ─────────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [savingTemplateName, setSavingTemplateName] = useState<string | null>(null);
  const [templateNameInput, setTemplateNameInput] = useState('');

  // ── Sent-this-session log (in-memory only, resets on reload) ─────────────
  const [sentLog, setSentLog] = useState<SentLogEntry[]>([]);
  const [sessionLogOpen, setSessionLogOpen] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  useEffect(() => {
    axios.get(`${API}/api/festivals/locations`, authHeader())
      .then((res) => setLocations(res.data.locations || []))
      .catch(() => showToast('Could not load locations'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = () => {
    return axios.get(`${API}/api/contacts`, authHeader())
      .then((res) => {
        const map = new Map<string, Contact>();
        (res.data.contacts || []).forEach((c: Contact) => map.set(normalize(c.name), c));
        setContactsByNorm(map);
      })
      .catch(() => showToast('Could not load contacts'));
  };

  const loadTemplates = () => {
    return axios.get(`${API}/api/templates`, authHeader())
      .then((res) => setTemplates(res.data.templates || []))
      .catch(() => showToast('Could not load templates'));
  };

  useEffect(() => {
    loadContacts();
    loadTemplates();
    // Signature defaults to the hardcoded DEFAULT_SIGNATURE, but if you've
    // saved a custom override on this device, that takes precedence.
    const savedSig = localStorage.getItem('outreach_signature');
    if (savedSig) setSignature(savedSig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { startDate, endDate };
      // ANY_LOCATION: omit locationIds entirely so EDMTrain returns all
      // locations, which we then sort by date client-side.
      if (locationId && locationId !== ANY_LOCATION) params.locationIds = String(locationId);
      if (eventName.trim()) params.eventName = eventName.trim();
      if (festivalsOnly) params.festivalInd = 'true';

      const res = await axios.get(`${API}/api/festivals/search`, { ...authHeader(), params });
      let events: Show[] = res.data.events || [];
      // Always present sorted by date ascending (matters most for any-location).
      events = [...events].sort((a, b) => a.date.localeCompare(b.date));
      setShows(events);
      setUploadedMode(false);
    } catch (err) {
      console.error(err);
      setError('Search failed - check dates/filters and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle an uploaded CSV of shows - builds the same Show[] the EDMTrain
  // search produces, for when a lineup is missing/incomplete on EDMTrain.
  const handleCsvUpload = (file: File) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        const rows = parseCsv(text);
        if (rows.length === 0) { setError('That CSV looked empty or had no data rows.'); return; }
        if (!('artist' in rows[0])) {
          setError('CSV needs an "artist" column (and optionally venue, city, date, festival, b2b_with).');
          return;
        }
        const { shows: builtShows, skipped } = csvRowsToShows(rows);
        if (builtShows.length === 0) { setError('No valid rows found (every row needs an artist name).'); return; }
        setShows(builtShows);
        setUploadedMode(true);
        showToast(`Loaded ${builtShows.length} show${builtShows.length !== 1 ? 's' : ''} from CSV${skipped ? ` (${skipped} row${skipped !== 1 ? 's' : ''} skipped)` : ''}`);
      } catch (err) {
        console.error(err);
        setError('Could not parse that CSV. Check the format and try again.');
      }
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(file);
  };

  // The sample rows shown/downloadable on the page so the format is obvious.
  const CSV_SAMPLE_ROWS = [
    ['artist', 'venue', 'city', 'date', 'festival', 'b2b_with'],
    ['Oliver Heldens', 'The Grand', 'Boston', '2026-06-26', '', ''],
    ['John Summit', 'Roadrunner', 'Boston', '2026-07-12', '', ''],
    ['Skrillex', 'Red Rocks', 'Denver', '2026-07-04', '', 'Fred Again..'],
    ['Charlotte de Witte', 'Brooklyn Mirage', 'Brooklyn', '2026-08-15', 'Mirage Festival', ''],
  ];

  const downloadCsvTemplate = () => {
    const csv = CSV_SAMPLE_ROWS.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shows_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getContact = (artistName: string) => contactsByNorm.get(normalize(artistName));
  const emailsFor = (contact: Contact) => {
    // Split, trim, drop blanks, and de-duplicate (case-insensitive) so a row
    // with the same address listed twice doesn't show/send it multiple times.
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of contact.emails.split(';')) {
      const e = raw.trim();
      if (!e) continue;
      const lc = e.toLowerCase();
      if (seen.has(lc)) continue;
      seen.add(lc);
      out.push(e);
    }
    return out;
  };

  // ── Inline add-contact ────────────────────────────────────────────────────
  const startAddContact = (artistName: string) => {
    setAddingContactFor(artistName);
    setNewContactEmail('');
  };

  const saveNewContact = async (artistName: string) => {
    const email = newContactEmail.trim();
    if (!email || !email.includes('@')) { showToast('Enter a valid email'); return; }
    setSavingContact(true);
    try {
      await axios.post(`${API}/api/contacts`, { name: artistName, emails: email }, authHeader());
      setContactsByNorm((prev) => {
        const next = new Map(prev);
        next.set(normalize(artistName), { name: artistName, emails: email, _rowNumber: -1 });
        return next;
      });
      setAddingContactFor(null);
      showToast('Contact added');
      loadContacts();
    } catch (err) {
      console.error(err);
      showToast('Failed to add contact');
    } finally {
      setSavingContact(false);
    }
  };

  // #1: append an additional email to an artist who already has a contact,
  // editing the existing sheet row (semicolon-appended) rather than adding a
  // new one.
  const saveAppendedEmail = async (artistName: string) => {
    const email = appendEmail.trim();
    if (!email || !email.includes('@')) { showToast('Enter a valid email'); return; }
    const existing = getContact(artistName);
    if (!existing || existing._rowNumber < 2) {
      // No known row number to edit (e.g. optimistically-added contact not yet
      // reconciled). Reload contacts and ask the user to retry.
      showToast('Reloading contacts — try again in a moment');
      loadContacts();
      return;
    }
    if (emailsFor(existing).some((e) => e.toLowerCase() === email.toLowerCase())) {
      showToast('That email is already on file');
      return;
    }
    setSavingContact(true);
    try {
      const merged = `${existing.emails}; ${email}`;
      await axios.patch(`${API}/api/contacts/${existing._rowNumber}`, { emails: merged }, authHeader());
      setContactsByNorm((prev) => {
        const next = new Map(prev);
        next.set(normalize(artistName), { ...existing, emails: merged });
        return next;
      });
      setAppendingFor(null);
      setAppendEmail('');
      showToast('Email added');
      loadContacts();
    } catch (err) {
      console.error(err);
      showToast('Failed to add email');
    } finally {
      setSavingContact(false);
    }
  };

  // #2: standalone add — add any name+email to the sheet, unrelated to a show.
  const saveStandaloneContact = async () => {
    const name = standaloneName.trim();
    const email = standaloneEmail.trim();
    if (!name) { showToast('Enter an artist / contact name'); return; }
    if (!email || !email.includes('@')) { showToast('Enter a valid email'); return; }
    setSavingContact(true);
    try {
      const existing = getContact(name);
      if (existing && existing._rowNumber >= 2) {
        // Name already exists — append to it instead of creating a duplicate row.
        if (emailsFor(existing).some((e) => e.toLowerCase() === email.toLowerCase())) {
          showToast('That contact + email already exists');
          setSavingContact(false);
          return;
        }
        const merged = `${existing.emails}; ${email}`;
        await axios.patch(`${API}/api/contacts/${existing._rowNumber}`, { emails: merged }, authHeader());
        showToast('Added to existing contact');
      } else {
        await axios.post(`${API}/api/contacts`, { name, emails: email }, authHeader());
        showToast('Contact added');
      }
      setStandaloneOpen(false);
      setStandaloneName('');
      setStandaloneEmail('');
      loadContacts();
    } catch (err) {
      console.error(err);
      showToast('Failed to add contact');
    } finally {
      setSavingContact(false);
    }
  };
  const recipientKey = (artistName: string, email: string) => `${artistName}::${email}`;

  const toggleEmail = (artist: Artist, show: Show, contact: Contact, email: string) => {
    const key = recipientKey(artist.name, email);
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Selecting as To: if this address was a CC on this artist's primary,
        // remove it from there first so it isn't both To and CC.
        for (const [k, r] of Array.from(next.entries())) {
          if (r.artistName === artist.name && r.cc.includes(email)) {
            next.set(k, { ...r, cc: r.cc.filter((c: string) => c !== email) });
          }
        }
        next.set(key, {
          key,
          artistName: artist.name,
          showName: show.name || show.venue?.name || 'this show',
          contactName: contact.name,
          email,
          cc: [],
          date: show.date,
          venue: [show.venue?.name, show.venue?.city].filter(Boolean).join(', '),
          venueName: show.venue?.name || '',
          city: show.venue?.city || '',
        });
      }
      return next;
    });
  };

  const toggleCc = (artistName: string, email: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      // The primary is this artist's first selected (To) recipient.
      const primary = Array.from(next.values()).find((r: Recipient) => r.artistName === artistName);
      if (!primary) { showToast('Check one address as "To" first, then CC the others'); return prev; }

      // Can't CC the same address that's already the To.
      if (primary.email.toLowerCase() === email.toLowerCase()) {
        showToast("That address is already the 'To' — CC a different one");
        return prev;
      }

      // If this address is currently selected as its OWN To (a second checkbox),
      // remove that separate send first - it's becoming a CC instead, so we
      // don't send it twice.
      const ownKey = recipientKey(artistName, email);
      if (next.has(ownKey)) next.delete(ownKey);

      const updated = { ...primary };
      if (updated.cc.includes(email)) updated.cc = updated.cc.filter((c) => c !== email);
      else updated.cc = [...updated.cc, email];
      next.set(primary.key, updated);
      return next;
    });
  };

  const isEmailSelected = (artistName: string, email: string) => selected.has(recipientKey(artistName, email));
  const isCc = (artistName: string, email: string) => {
    const primary = Array.from(selected.values()).find((r: Recipient) => r.artistName === artistName);
    return primary ? primary.cc.includes(email) : false;
  };
  const artistHasSelection = (artistName: string) =>
    Array.from(selected.values()).some((r: Recipient) => r.artistName === artistName);

  const togglePicker = (rowKey: string) => {
    setExpandedPicker((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  // Quick-select the first email of every artist on a show that has a contact.
  const selectAllWithContact = (show: Show) => {
    setSelected((prev) => {
      const next = new Map(prev);
      show.artists.forEach((artist) => {
        const contact = getContact(artist.name);
        if (!contact) return;
        const emails = emailsFor(contact);
        if (emails.length === 0) return;
        const email = emails[0];
        const key = recipientKey(artist.name, email);
        next.set(key, {
          key,
          artistName: artist.name,
          showName: show.name || show.venue?.name || 'this show',
          contactName: contact.name,
          email,
          cc: [],
          date: show.date,
          venue: [show.venue?.name, show.venue?.city].filter(Boolean).join(', '),
          venueName: show.venue?.name || '',
          city: show.venue?.city || '',
        });
      });
      return next;
    });
  };

  // ── Compose helpers ───────────────────────────────────────────────────────
  const insertAtCursor = (insertion: string, wrapSelection = false) => {
    // Route the insert to whichever field was last focused.
    if (lastFocused === 'subject') {
      const input = subjectRef.current;
      if (!input) { setSubject((s) => s + insertion.replace('%SELECTION%', '')); return; }
      const start = input.selectionStart ?? subject.length;
      const end = input.selectionEnd ?? subject.length;
      const cleaned = insertion.replace('%SELECTION%', wrapSelection ? subject.slice(start, end) : '');
      const newText = subject.slice(0, start) + cleaned + subject.slice(end);
      const cursorPos = start + cleaned.length;
      setSubject(newText);
      requestAnimationFrame(() => {
        input.focus();
        input.selectionStart = input.selectionEnd = cursorPos;
      });
      return;
    }

    const textarea = bodyRef.current;
    if (!textarea) { setBody((b) => b + insertion.replace('%SELECTION%', '')); return; }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    let newText: string;
    let cursorPos: number;
    if (wrapSelection && end > start) {
      const selectedText = body.slice(start, end);
      const replaced = insertion.replace('%SELECTION%', selectedText);
      newText = body.slice(0, start) + replaced + body.slice(end);
      cursorPos = start + replaced.length;
    } else {
      const cleaned = insertion.replace('%SELECTION%', '');
      newText = body.slice(0, start) + cleaned + body.slice(end);
      cursorPos = start + cleaned.length;
    }
    setBody(newText);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = cursorPos;
    });
  };

  const insertVariable = (token: string) => insertAtCursor(`{{${token}}}`);

  const insertLink = () => {
    const textarea = bodyRef.current;
    const hasSelection = textarea && textarea.selectionEnd > textarea.selectionStart;
    const url = window.prompt('Link URL (https://...)');
    if (!url) return;
    if (hasSelection) {
      insertAtCursor(`<a href="${url}">%SELECTION%</a>`, true);
    } else {
      const label = window.prompt('Link text', url) || url;
      insertAtCursor(`<a href="${url}">${label}</a>`);
    }
  };

  const saveSignature = () => {
    localStorage.setItem('outreach_signature', signature);
    setShowSignatureEditor(false);
    showToast('Signature saved');
  };

  const personalizeGrouped = (template: string, g: GroupedRecipient) => {
    // {{name}} derives from the email's local-part, with a "<artist>'s team"
    // fallback for generic mailboxes. An explicit per-recipient override
    // (set in the name-check panel) always wins.
    const derivedName = nameOverrides.get(g.email)
      ?? deriveGreetingName(g.email, g.artistNames.join(' & '));
    return template
      .replace(/{{\s*name\s*}}/gi, derivedName)
      .replace(/{{\s*artist\s*}}/gi, g.artistNames.join(', '))
      .replace(/{{\s*festival\s*}}/gi, g.showNames.join(', '))
      // shortdate MUST be replaced before date, or the {{date}} regex would
      // match the "date" inside "shortdate" and corrupt it.
      .replace(/{{\s*shortdate\s*}}/gi, g.dates.map(formatDateShort).join(', '))
      .replace(/{{\s*date\s*}}/gi, g.dates.map(formatDateLong).join(', '))
      // shortvenue before venue, same substring-collision reason as shortdate.
      // Falls back to the full venue string if a bare name wasn't available.
      .replace(/{{\s*shortvenue\s*}}/gi, (g.venueNames.length ? g.venueNames : g.venues).join(', '))
      .replace(/{{\s*venue\s*}}/gi, g.venues.join(', '))
      .replace(/{{\s*city\s*}}/gi, g.cities.join(', '));
  };

  const recipients = useMemo(() => Array.from(selected.values()), [selected]);
  const groupedRecipients = useMemo(() => groupRecipientsByEmail(recipients), [recipients]);

  // Remove an entire grouped recipient (all selected entries sharing this email).
  const removeGroupedRecipient = (email: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const [k, r] of Array.from(next.entries())) {
        if (r.email.toLowerCase() === email.toLowerCase()) next.delete(k);
      }
      return next;
    });
  };

  // Remove one artist from a grouped recipient (e.g. a shared-manager email
  // covers 3 artists and you want to drop one). Removes that artist's selected
  // entry; if it was the last one for this email, the group disappears.
  const removeArtistFromGroup = (email: string, artistName: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const [k, r] of Array.from(next.entries())) {
        if (r.email.toLowerCase() === email.toLowerCase() && r.artistName === artistName) next.delete(k);
      }
      return next;
    });
  };
  // Clamp the preview index so it stays valid as the recipient list changes.
  const safePreviewIdx = Math.min(previewIdx, Math.max(0, groupedRecipients.length - 1));
  const previewGroup = groupedRecipients[safePreviewIdx];

  const getEffectiveSubject = (g: GroupedRecipient) =>
    subjectOverrides.get(g.email) ?? personalizeGrouped(subject, g);

  const setSubjectOverride = (email: string, value: string) => {
    setSubjectOverrides((prev) => {
      const next = new Map(prev);
      next.set(email, value);
      return next;
    });
  };

  // The name that {{name}} will resolve to for a given recipient: the manual
  // override if set, else the auto-derived one.
  const getEffectiveName = (g: GroupedRecipient) =>
    nameOverrides.get(g.email) ?? deriveGreetingName(g.email, g.artistNames.join(' & '));

  const setNameOverride = (email: string, value: string) => {
    setNameOverrides((prev) => {
      const next = new Map(prev);
      next.set(email, value);
      return next;
    });
  };

  // Body with signature appended, then personalized. Respects a per-recipient
  // hand-edited override ("Edit this one") when present.
  const buildBody = (g: GroupedRecipient) => {
    const override = bodyOverrides.get(g.email);
    const base = override ?? body;
    const full = signature.trim() ? `${base}\n\n${signature}` : base;
    return personalizeGrouped(full, g);
  };

  // Same as personalizeGrouped, but wraps each substituted value in a colored
  // span so the reviewer can see at a glance what got personalized. Preview
  // only - never used for the actual send.
  const personalizeHighlighted = (template: string, g: GroupedRecipient) => {
    const hl = (v: string) => `<span style="background:#fdf07a;color:#111;border-radius:3px;padding:0 3px">${v}</span>`;
    const derivedName = nameOverrides.get(g.email) ?? deriveGreetingName(g.email, g.artistNames.join(' & '));
    return template
      .replace(/{{\s*name\s*}}/gi, hl(derivedName))
      .replace(/{{\s*artist\s*}}/gi, hl(g.artistNames.join(', ')))
      .replace(/{{\s*festival\s*}}/gi, hl(g.showNames.join(', ')))
      .replace(/{{\s*shortdate\s*}}/gi, hl(g.dates.map(formatDateShort).join(', ')))
      .replace(/{{\s*date\s*}}/gi, hl(g.dates.map(formatDateLong).join(', ')))
      .replace(/{{\s*shortvenue\s*}}/gi, hl((g.venueNames.length ? g.venueNames : g.venues).join(', ')))
      .replace(/{{\s*venue\s*}}/gi, hl(g.venues.join(', ')))
      .replace(/{{\s*city\s*}}/gi, hl(g.cities.join(', ')));
  };

  // Preview body with highlighting applied.
  const buildBodyHighlighted = (g: GroupedRecipient) => {
    const override = bodyOverrides.get(g.email);
    const base = override ?? body;
    const full = signature.trim() ? `${base}\n\n${signature}` : base;
    return personalizeHighlighted(full, g);
  };

  const setBodyOverride = (email: string, value: string) => {
    setBodyOverrides((prev) => {
      const next = new Map(prev);
      next.set(email, value);
      return next;
    });
  };

  const toggleApprove = (email: string) => {
    setApprovedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const allApproved = groupedRecipients.length > 0 && groupedRecipients.every((g) => approvedEmails.has(g.email));

  // ── Templates ─────────────────────────────────────────────────────────────
  const applyTemplate = (name: string) => {
    setSelectedTemplate(name);
    const tpl = templates.find((t) => t.name === name);
    if (tpl) { setSubject(tpl.subject); setBody(tpl.body); }
  };

  const saveTemplate = async () => {
    const name = templateNameInput.trim();
    if (!name) { showToast('Enter a template name'); return; }
    if (!subject.trim() || !body.trim()) { showToast('Write a subject and body first'); return; }
    try {
      await axios.post(`${API}/api/templates`, { name, subject, body }, authHeader());
      showToast('Template saved');
      setSavingTemplateName(null);
      setTemplateNameInput('');
      setSelectedTemplate(name); // select the just-saved one
      loadTemplates();
    } catch (err) {
      console.error(err);
      showToast('Failed to save template');
    }
  };

  // Update the currently-loaded template in place (same name = backend upsert
  // overwrites it) rather than creating a new one.
  const updateCurrentTemplate = async () => {
    if (!selectedTemplate) { showToast('Load a template first'); return; }
    if (!subject.trim() || !body.trim()) { showToast('Write a subject and body first'); return; }
    try {
      await axios.post(`${API}/api/templates`, { name: selectedTemplate, subject, body }, authHeader());
      showToast(`Updated "${selectedTemplate}"`);
      loadTemplates();
    } catch (err) {
      console.error(err);
      showToast('Failed to update template');
    }
  };

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) { showToast('Subject and body are required'); return; }
    if (groupedRecipients.length === 0) { showToast('Select at least one recipient'); return; }
    if (!allApproved) { showToast('Approve every recipient in Preview first'); return; }
    setSending(true);
    const logEntries: SentLogEntry[] = [];
    try {
      if (groupedRecipients.length === 1) {
        const g = groupedRecipients[0];
        const finalSubject = getEffectiveSubject(g);
        try {
          await axios.post(`${API}/api/outreach/send`,
            { to: g.email, cc: g.cc, subject: finalSubject, html: buildBody(g) }, authHeader());
          logEntries.push({ to: g.email, subject: finalSubject, sentAt: new Date().toLocaleTimeString(), ok: true });
          showToast('Sent!');
        } catch (err) {
          logEntries.push({ to: g.email, subject: finalSubject, sentAt: new Date().toLocaleTimeString(), ok: false, error: 'Send failed' });
          showToast('Send failed');
        }
      } else if (staggerSend) {
        // Staggered: send one at a time with a delay between, to look human
        // and protect domain reputation on larger batches. Runs client-side,
        // so the tab must stay open until it finishes.
        let sent = 0, failed = 0;
        for (let i = 0; i < groupedRecipients.length; i++) {
          const g = groupedRecipients[i];
          const finalSubject = getEffectiveSubject(g);
          setSendProgress(`Sending ${i + 1} of ${groupedRecipients.length}…`);
          try {
            await axios.post(`${API}/api/outreach/send`,
              { to: g.email, cc: g.cc, subject: finalSubject, html: buildBody(g) }, authHeader());
            logEntries.push({ to: g.email, subject: finalSubject, sentAt: new Date().toLocaleTimeString(), ok: true });
            sent++;
          } catch (err) {
            logEntries.push({ to: g.email, subject: finalSubject, sentAt: new Date().toLocaleTimeString(), ok: false, error: 'Send failed' });
            failed++;
          }
          // Wait between sends (but not after the last one).
          if (i < groupedRecipients.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, STAGGER_DELAY_MS));
          }
        }
        setSendProgress('');
        showToast(`Sent ${sent}, failed ${failed}`);
      } else {
        const emails = groupedRecipients.map((g) => ({ to: g.email, cc: g.cc, subject: getEffectiveSubject(g), html: buildBody(g) }));
        const res = await axios.post(`${API}/api/outreach/send-batch`, { emails }, authHeader());
        (res.data.results || []).forEach((r: { to: string; ok: boolean; error?: string }) => {
          const match = groupedRecipients.find((g) => g.email === r.to);
          logEntries.push({
            to: r.to,
            subject: match ? getEffectiveSubject(match) : subject,
            sentAt: new Date().toLocaleTimeString(),
            ok: r.ok,
            error: r.error,
          });
        });
        showToast(`Sent ${res.data.sent}, failed ${res.data.failed}`);
      }
      setSentLog((prev) => [...logEntries, ...prev]);
      setSelected(new Map());
      setComposing(false);
      setSubject('');
      setBody('');
      setSelectedTemplate('');
      setSubjectOverrides(new Map());
      setCustomizingSubjects(false);
      setNameOverrides(new Map());
      setCheckingNames(false);
      setApprovedEmails(new Set());
      setBodyOverrides(new Map());
      setPreviewIdx(0);
      setEditingThisOne(false);
    } catch (err) {
      console.error(err);
      showToast('Send failed - check the console');
    } finally {
      setSending(false);
    }
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <>
      <style>{css}</style>
      <motion.div
        style={{ minHeight: '100vh', backgroundColor: '#080808', padding: '64px 48px 200px', fontFamily: 'Montserrat, sans-serif' }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.26em', color: '#4db0f2', textTransform: 'uppercase', margin: '0 0 12px' }}>Outreach</p>
          <h1 style={{ fontFamily: 'Azonix, sans-serif', fontSize: '2.4rem', color: '#fff', margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Shows &amp; Outreach</h1>
          <div className="divider" />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-confirm" onClick={() => { setStandaloneOpen(true); setStandaloneName(''); setStandaloneEmail(''); }}>
              + Add contact to database
            </button>
          </div>

          {/* ── Sent this session ── */}
          <div className="session-toggle" onClick={() => setSessionLogOpen((o) => !o)}>
            <span>Sent this session ({sentLog.length})</span>
            <span style={{ color: '#9a9a9a', fontSize: '0.8rem' }}>{sessionLogOpen ? '▾' : '▸'}</span>
          </div>
          {sessionLogOpen && (
            <div style={{ border: '1px solid #2a2a2a', borderRadius: 4, marginBottom: 20, background: '#0d0d0d' }}>
              {sentLog.length === 0 ? (
                <p style={{ padding: 16, fontSize: '0.8rem', color: '#9a9a9a', margin: 0 }}>Nothing sent yet this session.</p>
              ) : (
                sentLog.map((entry, i) => (
                  <div key={i} className="session-log-item">
                    <span className={entry.ok ? 'ok' : 'fail'}>{entry.ok ? '✓' : '✗'} {entry.to}</span>
                    <span style={{ color: '#9a9a9a' }}>{entry.subject}</span>
                    <span style={{ color: '#9a9a9a' }}>{entry.sentAt}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Search bar ── */}
          <div className="search-bar">
            <div className="search-field">
              <label>City</label>
              <select className="admin-select" value={locationId} onChange={(e) => setLocationId(Number(e.target.value))} style={{ minWidth: 210 }}>
                <option value={DEFAULT_LOCATION_ID}>{DEFAULT_LOCATION_LABEL}</option>
                <option value={ANY_LOCATION}>Any location (by date)</option>
                {locations
                  .filter((l) => l.id !== DEFAULT_LOCATION_ID)
                  .slice()
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
              </select>
            </div>

            <div className="search-field">
              <label>Festival / event name</label>
              <input className="admin-input" placeholder="e.g. Ultra" value={eventName} onChange={(e) => setEventName(e.target.value)} style={{ minWidth: 190 }} />
            </div>

            <div className="search-field">
              <label>From</label>
              <input className="admin-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()} />
            </div>

            <div className="search-field">
              <label>To</label>
              <input className="admin-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()} />
            </div>

            <button className={festivalsOnly ? 'btn btn-toggle-on' : 'btn'} onClick={() => setFestivalsOnly((f) => !f)}>
              {festivalsOnly ? '✓ Festivals only' : 'Festivals only'}
            </button>

            <button className="btn btn-confirm" onClick={runSearch} disabled={loading} style={{ marginLeft: 'auto' }}>
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>

          {/* CSV upload - for shows where EDMTrain's lineup is missing/incomplete */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                if (file) handleCsvUpload(file);
                e.target.value = ''; // allow re-uploading the same file
              }}
            />
            <button className="btn" onClick={() => csvInputRef.current?.click()}>Upload CSV of shows</button>
            <button className="btn" onClick={() => setShowCsvSample((s) => !s)}>
              {showCsvSample ? 'Hide sample' : 'View sample format'} {showCsvSample ? '▾' : '▸'}
            </button>
            {uploadedMode && (
              <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => { setShows([]); setUploadedMode(false); runSearch(); }}>
                ← Back to EDMTrain search
              </button>
            )}
          </div>

          {showCsvSample && (
            <div style={{ border: '1px solid #2a2a2a', borderRadius: 4, background: '#0d0d0d', padding: 16, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ fontSize: '0.72rem', color: '#c8c8c8' }}>
                  Each row is one artist at one show. Rows with the same venue + city + date group into one show card.
                </span>
                <button className="btn btn-confirm" style={{ padding: '7px 13px' }} onClick={downloadCsvTemplate}>Download template</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.72rem' }}>
                  <thead>
                    <tr>
                      {CSV_SAMPLE_ROWS[0].map((h) => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #333', color: '#a6d7f8', whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {CSV_SAMPLE_ROWS.slice(1).map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci} style={{ padding: '6px 10px', borderBottom: '1px solid #1a1a1a', color: cell ? '#dcdcdc' : '#555', whiteSpace: 'nowrap' }}>
                            {cell || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '0.66rem', color: '#8a8a8a', marginTop: 12, lineHeight: 1.6 }}>
                <div><code style={{ color: '#a6d7f8' }}>date</code> — format as YYYY-MM-DD (e.g. 2026-06-26), so {'{{date}}'} and {'{{shortdate}}'} fill in correctly.</div>
                <div><code style={{ color: '#a6d7f8' }}>festival</code> — optional; leave blank for a club show, fill it to tag the card as a festival.</div>
                <div><code style={{ color: '#a6d7f8' }}>b2b_with</code> — optional; for a back-to-back set, name the other artist here and you'll be able to email both their managers.</div>
                <div style={{ marginTop: 4 }}>Only <code style={{ color: '#a6d7f8' }}>artist</code> is required — the rest can be blank if you don't have them.</div>
              </div>
            </div>
          )}

          {uploadedMode && (
            <p style={{ fontSize: '0.72rem', color: '#a6d7f8', marginBottom: 12 }}>
              Showing {shows.length} uploaded show{shows.length !== 1 ? 's' : ''} from your CSV.
            </p>
          )}

          {error && <p style={{ color: '#ff8888', fontSize: '0.85rem', marginBottom: 16 }}>{error}</p>}
          {!loading && shows.length === 0 && !error && (
            <p style={{ color: '#9a9a9a', fontSize: '0.9rem' }}>No shows found for this search.</p>
          )}

          {shows.map((show) => {
            const badge = formatDateBadge(show.date);
            const hasAnyContact = show.artists.some((a) => getContact(a.name));
            return (
              <div key={show.id} className="show-card">
                <div className="date-badge">
                  <span className="month">{badge.month}</span>
                  <span className="day">{badge.day}</span>
                  <span className="year">{badge.year}</span>
                </div>
                <div className="show-body">
                  <div className="show-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div className="show-title-row">
                        <span className="show-title">{show.name || show.venue?.name || 'Untitled show'}</span>
                        {show.isFestival && <span className="festival-tag">Festival</span>}
                      </div>
                      <div className="show-venue">
                        {formatDateLong(show.date)} · {show.venue?.name}{show.venue?.city ? `, ${show.venue.city}` : ''}
                      </div>
                    </div>
                    {hasAnyContact && (
                      <button className="btn" style={{ padding: '7px 13px', fontSize: '0.64rem' }} onClick={() => selectAllWithContact(show)}>
                        Select all with contact
                      </button>
                    )}
                  </div>

                  {show.artists.length === 0 ? (
                    <div className="artist-row"><span className="artist-name" style={{ color: '#9a9a9a' }}>Lineup not listed yet</span></div>
                  ) : (
                    show.artists.map((artist, artistIdx) => {
                      const contact = getContact(artist.name);
                      const isAdding = addingContactFor === artist.name;
                      const rowKey = `${show.id}-${artist.id}-${artistIdx}`;
                      const emails = contact ? emailsFor(contact) : [];
                      const pickerOpen = expandedPicker.has(rowKey);
                      const selectedHere = contact ? artistHasSelection(artist.name) : false;
                      return (
                        <div key={rowKey} className="artist-row">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                            <span className="artist-name">{artist.name}{artist.b2b ? ' b2b' : ''}</span>
                            {contact ? (
                              emails.length === 1 ? (
                                <>
                                  <span
                                    className="contact-pill"
                                    title="Click to show the email"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => togglePicker(rowKey)}
                                  >
                                    Have contact {pickerOpen ? '▾' : '▸'}
                                  </span>
                                  <input type="checkbox" checked={isEmailSelected(artist.name, emails[0])} onChange={() => toggleEmail(artist, show, contact, emails[0])} />
                                  <span className="no-contact-pill" style={{ fontSize: '0.58rem', padding: '3px 8px' }} onClick={() => { setAppendingFor(artist.name); setAppendEmail(''); }}>+ email</span>
                                </>
                              ) : (
                                <span
                                  className="contact-pill"
                                  onClick={() => togglePicker(rowKey)}
                                  title="Multiple emails on file"
                                >
                                  {selectedHere ? '✓ ' : ''}{emails.length} emails {pickerOpen ? '▾' : '▸'}
                                </span>
                              )
                            ) : isAdding ? (
                              <div className="add-contact-inline">
                                <input
                                  className="admin-input"
                                  style={{ width: 240, padding: '7px 11px', fontSize: '0.82rem' }}
                                  placeholder="email address"
                                  value={newContactEmail}
                                  onChange={(e) => setNewContactEmail(e.target.value)}
                                  autoFocus
                                />
                                <button className="btn btn-confirm" style={{ padding: '7px 13px' }} onClick={() => saveNewContact(artist.name)} disabled={savingContact}>
                                  {savingContact ? '…' : 'Save'}
                                </button>
                                <button className="btn" style={{ padding: '7px 13px' }} onClick={() => setAddingContactFor(null)}>Cancel</button>
                              </div>
                            ) : (
                              <span className="no-contact-pill" onClick={() => startAddContact(artist.name)}>+ Add contact</span>
                            )}
                          </div>

                          {/* Multi-email picker: choose To / CC per address */}
                          {contact && emails.length > 1 && pickerOpen && (
                            <div className="email-picker">
                              {emails.map((email) => (
                                <div key={email} className="email-option">
                                  <input
                                    type="checkbox"
                                    checked={isEmailSelected(artist.name, email)}
                                    onChange={() => toggleEmail(artist, show, contact, email)}
                                  />
                                  <span className="addr">{email}</span>
                                  <span
                                    className={`cc-toggle ${isCc(artist.name, email) ? 'on' : ''}`}
                                    onClick={() => toggleCc(artist.name, email)}
                                  >
                                    {isCc(artist.name, email) ? '✓ CC' : 'CC'}
                                  </span>
                                </div>
                              ))}
                              {appendingFor === artist.name ? (
                                <div className="add-contact-inline" style={{ marginTop: 8 }}>
                                  <input
                                    className="admin-input"
                                    style={{ width: 240, padding: '7px 11px', fontSize: '0.82rem' }}
                                    placeholder="additional email"
                                    value={appendEmail}
                                    onChange={(e) => setAppendEmail(e.target.value)}
                                    autoFocus
                                  />
                                  <button className="btn btn-confirm" style={{ padding: '7px 13px' }} onClick={() => saveAppendedEmail(artist.name)} disabled={savingContact}>
                                    {savingContact ? '…' : 'Add'}
                                  </button>
                                  <button className="btn" style={{ padding: '7px 13px' }} onClick={() => { setAppendingFor(null); setAppendEmail(''); }}>Cancel</button>
                                </div>
                              ) : (
                                <span className="no-contact-pill" style={{ marginTop: 8, display: 'inline-block' }} onClick={() => { setAppendingFor(artist.name); setAppendEmail(''); }}>+ Add another email</span>
                              )}
                              <p style={{ fontSize: '0.66rem', color: '#8a8a8a', margin: '6px 0 0' }}>
                                Check an address to email it (To). Tap CC to copy it on this artist's primary send instead.
                              </p>
                            </div>
                          )}

                          {/* Single-email contact: reveal the address when the pill is clicked */}
                          {contact && emails.length === 1 && pickerOpen && (
                            <div className="email-picker">
                              <div className="email-option">
                                <span className="addr" style={{ userSelect: 'all' }}>{emails[0]}</span>
                              </div>
                            </div>
                          )}

                          {/* Single-email contact: allow adding a second email */}
                          {contact && emails.length === 1 && appendingFor === artist.name && (
                            <div className="add-contact-inline" style={{ marginTop: 8 }}>
                              <input
                                className="admin-input"
                                style={{ width: 240, padding: '7px 11px', fontSize: '0.82rem' }}
                                placeholder="additional email"
                                value={appendEmail}
                                onChange={(e) => setAppendEmail(e.target.value)}
                                autoFocus
                              />
                              <button className="btn btn-confirm" style={{ padding: '7px 13px' }} onClick={() => saveAppendedEmail(artist.name)} disabled={savingContact}>
                                {savingContact ? '…' : 'Add'}
                              </button>
                              <button className="btn" style={{ padding: '7px 13px' }} onClick={() => { setAppendingFor(null); setAppendEmail(''); }}>Cancel</button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}

          {recipients.length > 0 && !composing && (
            <div style={{ position: 'fixed', bottom: 32, right: 48, zIndex: 140, display: 'flex', gap: 10 }}>
              <button className="btn" onClick={() => setShowSelectedList((s) => !s)}>
                Review selected ({groupedRecipients.length})
              </button>
              <button className="btn btn-confirm" onClick={() => setComposing(true)}>
                Compose outreach ({groupedRecipients.length} email{groupedRecipients.length !== 1 ? 's' : ''})
              </button>
            </div>
          )}

          {/* Selected-recipients review list - prune before composing */}
          {showSelectedList && recipients.length > 0 && !composing && (
            <div style={{ position: 'fixed', bottom: 88, right: 48, zIndex: 141, width: 420, maxHeight: '60vh', overflowY: 'auto', background: '#0d0d0d', border: '1px solid #4db0f2', borderRadius: 6, boxShadow: '0 -8px 30px rgba(0,0,0,0.7)', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#c8c8c8' }}>
                  {groupedRecipients.length} email{groupedRecipients.length !== 1 ? 's' : ''} · {recipients.length} artist{recipients.length !== 1 ? 's' : ''}
                </span>
                <button className="btn" style={{ padding: '5px 10px', fontSize: '0.6rem' }} onClick={() => setShowSelectedList(false)}>Close</button>
              </div>
              {groupedRecipients.map((g) => (
                <div key={g.email} style={{ borderBottom: '1px solid #1a1a1a', padding: '10px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '0.8rem', color: '#e4e4e4', wordBreak: 'break-all' }}>{g.email}</span>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '4px 9px', fontSize: '0.58rem', flexShrink: 0 }}
                      onClick={() => removeGroupedRecipient(g.email)}
                      title="Remove this whole email from the batch"
                    >
                      ✕ remove
                    </button>
                  </div>
                  {g.cc.length > 0 && (
                    <div style={{ fontSize: '0.66rem', color: '#e1c679', marginTop: 2 }}>cc: {g.cc.join(', ')}</div>
                  )}
                  <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {g.artistNames.map((an) => (
                      <span key={an} style={{ fontSize: '0.66rem', color: '#a8a8a8', background: '#161616', borderRadius: 12, padding: '2px 9px', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        {an}
                        {g.artistNames.length > 1 && (
                          <span
                            style={{ cursor: 'pointer', color: '#d08080', fontWeight: 700 }}
                            onClick={() => removeArtistFromGroup(g.email, an)}
                            title={`Drop ${an} from this email`}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Compose panel ── */}
      {composing && (
        <div className="compose-panel">
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div className="compose-row" style={{ justifyContent: 'space-between' }}>
              <p style={{ fontSize: '0.7rem', letterSpacing: '0.16em', color: '#b8b8b8', textTransform: 'uppercase', margin: 0 }}>
                Compose outreach — {groupedRecipients.length} email{groupedRecipients.length !== 1 ? 's' : ''} ({recipients.length} artist{recipients.length !== 1 ? 's' : ''})
              </p>
              <button className="btn" onClick={() => setComposing(false)}>Close</button>
            </div>

            {/* Templates */}
            <div className="compose-row" style={{ alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: '0.6rem', color: '#9a9a9a', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 5 }}>Template</label>
                <select className="admin-select" value={selectedTemplate} onChange={(e) => applyTemplate(e.target.value)} style={{ minWidth: 220 }}>
                  <option value="">Start from scratch…</option>
                  {templates.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              {selectedTemplate && (
                <button
                  className="btn btn-danger"
                  onClick={async () => {
                    const tpl = templates.find((t) => t.name === selectedTemplate);
                    if (!tpl) return;
                    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
                    try {
                      await axios.delete(`${API}/api/templates/${tpl._rowNumber}`, authHeader());
                      showToast('Template deleted');
                      setSelectedTemplate('');
                      loadTemplates();
                    } catch (err) { console.error(err); showToast('Failed to delete'); }
                  }}
                >
                  Delete
                </button>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                {/* Update-in-place: only when a template is loaded */}
                {selectedTemplate && savingTemplateName === null && (
                  <button className="btn btn-confirm" onClick={updateCurrentTemplate} title={`Overwrite "${selectedTemplate}" with your current edits`}>
                    Update "{selectedTemplate}"
                  </button>
                )}
                {savingTemplateName === null ? (
                  <button className="btn" onClick={() => { setSavingTemplateName(''); setTemplateNameInput(''); }}>
                    Save as new
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input className="admin-input" placeholder="New template name" value={templateNameInput} onChange={(e) => setTemplateNameInput(e.target.value)} style={{ width: 190 }} />
                    <button className="btn btn-confirm" onClick={saveTemplate}>Save</button>
                    <button className="btn" onClick={() => setSavingTemplateName(null)}>Cancel</button>
                  </div>
                )}
              </div>
            </div>

            <div className="compose-row">
              <input ref={subjectRef} className="admin-input" style={{ flex: 1, fontSize: '1.12rem', padding: '13px 16px' }} placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} onFocus={() => setLastFocused('subject')} />
              {groupedRecipients.length > 1 && (
                <button className={customizingSubjects ? 'btn btn-toggle-on' : 'btn'} onClick={() => setCustomizingSubjects((c) => !c)}>
                  {customizingSubjects ? '✓ Customizing subjects' : 'Customize per-recipient'}
                </button>
              )}
            </div>

            {customizingSubjects && groupedRecipients.length > 1 && (
              <div style={{ border: '1px solid #2a2a2a', borderRadius: 4, marginBottom: 14, maxHeight: 220, overflowY: 'auto' }}>
                {groupedRecipients.map((g) => (
                  <div key={g.email} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #1a1a1a' }}>
                    <div style={{ minWidth: 180, maxWidth: 220 }}>
                      <div style={{ fontSize: '0.78rem', color: '#dcdcdc' }}>{g.email}</div>
                      <div style={{ fontSize: '0.66rem', color: '#9a9a9a' }}>{g.artistNames.join(', ')}</div>
                    </div>
                    <input
                      className="admin-input"
                      style={{ flex: 1, fontSize: '0.82rem' }}
                      value={getEffectiveSubject(g)}
                      onChange={(e) => setSubjectOverride(g.email, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Name check: confirm/edit the {{name}} greeting per recipient */}
            {groupedRecipients.length > 0 && (
              <div className="compose-row" style={{ marginBottom: checkingNames ? 6 : 12 }}>
                <button className={checkingNames ? 'btn btn-toggle-on' : 'btn'} onClick={() => setCheckingNames((c) => !c)}>
                  {checkingNames ? '✓ Checking names' : 'Check names'}
                </button>
                <span style={{ fontSize: '0.66rem', color: '#8a8a8a' }}>
                  Auto-fills {'{{name}}'} from each email; generic inboxes become "[artist]'s team". Review before sending.
                </span>
              </div>
            )}
            {checkingNames && groupedRecipients.length > 0 && (
              <div style={{ border: '1px solid #2a2a2a', borderRadius: 4, marginBottom: 14, maxHeight: 240, overflowY: 'auto' }}>
                {groupedRecipients.map((g) => {
                  const derived = deriveGreetingName(g.email, g.artistNames.join(' & '));
                  const isTeamFallback = derived.endsWith("'s team") || derived === 'there';
                  return (
                    <div key={g.email} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #1a1a1a' }}>
                      <div style={{ minWidth: 190, maxWidth: 230 }}>
                        <div style={{ fontSize: '0.78rem', color: '#dcdcdc' }}>{g.email}</div>
                        <div style={{ fontSize: '0.66rem', color: '#9a9a9a' }}>{g.artistNames.join(', ')}</div>
                      </div>
                      <span style={{ fontSize: '0.66rem', color: isTeamFallback ? '#e1c679' : '#8ee9ac', minWidth: 70 }}>
                        {isTeamFallback ? 'generic →' : 'name →'}
                      </span>
                      <input
                        className="admin-input"
                        style={{ flex: 1, fontSize: '0.82rem' }}
                        value={getEffectiveName(g)}
                        onChange={(e) => setNameOverride(g.email, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="compose-row" style={{ marginBottom: 6 }}>
              <span style={{ fontSize: '0.66rem', color: '#9a9a9a', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Insert:</span>
              <button className="var-btn" onClick={() => insertVariable('name')}>{'{{name}}'}</button>
              <button className="var-btn" onClick={() => insertVariable('artist')}>{'{{artist}}'}</button>
              <button className="var-btn" onClick={() => insertVariable('festival')}>{'{{festival}}'}</button>
              <button className="var-btn" onClick={() => insertVariable('date')}>{'{{date}}'}</button>
              <button className="var-btn" onClick={() => insertVariable('shortdate')}>{'{{shortdate}}'}</button>
              <button className="var-btn" onClick={() => insertVariable('venue')}>{'{{venue}}'}</button>
              <button className="var-btn" onClick={() => insertVariable('shortvenue')}>{'{{shortvenue}}'}</button>
              <button className="var-btn" onClick={() => insertVariable('city')}>{'{{city}}'}</button>
              <button className="link-btn" onClick={insertLink}>+ Link</button>
            </div>

            {/* Write / Preview tabs */}
            <div className="compose-row" style={{ marginBottom: 4 }}>
              <button className={`btn-tab ${composeView === 'write' ? 'btn-tab-active' : ''}`} style={{ color: composeView === 'write' ? '#fff' : '#9a9a9a', cursor: 'pointer', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase' }} onClick={() => setComposeView('write')}>Write</button>
              <button className={`btn-tab ${composeView === 'preview' ? 'btn-tab-active' : ''}`} style={{ color: composeView === 'preview' ? '#fff' : '#9a9a9a', cursor: 'pointer', fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase' }} onClick={() => setComposeView('preview')}>Preview</button>
            </div>

            {composeView === 'write' ? (
              <div className="compose-row" style={{ display: 'block' }}>
                <textarea ref={bodyRef} className="admin-textarea" rows={7} placeholder="Hey {{name}}, ... (select text then + Link to hyperlink it; raw HTML is allowed)" value={body} onChange={(e) => setBody(e.target.value)} onFocus={() => setLastFocused('body')} />
                {bodyOverrides.size > 0 && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(225,198,121,0.1)', border: '1px solid #4a4020', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.72rem', color: '#e1c679' }}>
                      {bodyOverrides.size} recipient{bodyOverrides.size !== 1 ? 's have' : ' has'} a custom-edited body that <strong>won't</strong> update with your changes above.
                    </span>
                    <button
                      className="btn"
                      style={{ padding: '6px 12px', fontSize: '0.66rem' }}
                      onClick={() => {
                        if (window.confirm(`Reset ${bodyOverrides.size} custom-edited recipient${bodyOverrides.size !== 1 ? 's' : ''} back to this shared body? Their individual edits will be lost.`)) {
                          setBodyOverrides(new Map());
                          showToast('All recipients reset to the shared body');
                        }
                      }}
                    >
                      Apply this body to all (reset custom edits)
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="compose-row" style={{ display: 'block' }}>
                {previewGroup ? (
                  <>
                    {/* Review header: which recipient, Next/Back, approve state */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                      <button className="btn" disabled={safePreviewIdx === 0} onClick={() => { setPreviewIdx((i) => Math.max(0, i - 1)); setEditingThisOne(false); }}>← Back</button>
                      <span style={{ fontSize: '0.72rem', color: '#c8c8c8' }}>
                        Recipient {safePreviewIdx + 1} of {groupedRecipients.length}
                      </span>
                      <button className="btn" disabled={safePreviewIdx >= groupedRecipients.length - 1} onClick={() => { setPreviewIdx((i) => Math.min(groupedRecipients.length - 1, i + 1)); setEditingThisOne(false); }}>Next →</button>
                      {groupedRecipients.length > 1 && (
                        <button
                          className={allApproved ? 'btn btn-toggle-on' : 'btn'}
                          onClick={() => {
                            if (allApproved) setApprovedEmails(new Set());
                            else setApprovedEmails(new Set(groupedRecipients.map((g) => g.email)));
                          }}
                        >
                          {allApproved ? 'Unapprove all' : 'Approve all'}
                        </button>
                      )}
                      <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: allApproved ? '#7ed9a0' : '#9a9a9a' }}>
                        {approvedEmails.size} / {groupedRecipients.length} approved
                      </span>
                    </div>

                    <p style={{ fontSize: '0.74rem', color: '#a6d7f8', marginBottom: 6 }}>
                      To: <strong>{previewGroup.email}</strong>
                      {previewGroup.cc.length > 0 && <span style={{ color: '#e1c679' }}> · cc: {previewGroup.cc.join(', ')}</span>}
                      {previewGroup.artistNames.length > 1 && <span style={{ color: '#9a9a9a' }}> · covers: {previewGroup.artistNames.join(', ')}</span>}
                    </p>

                    <p style={{ fontSize: '0.78rem', color: '#c8c8c8', marginBottom: 8 }}>
                      <strong>Subject:</strong>{' '}
                      <span dangerouslySetInnerHTML={{ __html: personalizeHighlighted(getEffectiveSubject(previewGroup), previewGroup) || '<em>(no subject yet)</em>' }} />
                    </p>

                    {editingThisOne ? (
                      <>
                        <textarea
                          className="admin-textarea"
                          rows={8}
                          value={bodyOverrides.get(previewGroup.email) ?? body}
                          onChange={(e) => setBodyOverride(previewGroup.email, e.target.value)}
                        />
                        <p style={{ fontSize: '0.66rem', color: '#8a8a8a', margin: '6px 0 0' }}>
                          Editing only <strong>{previewGroup.email}</strong>'s copy. Variables still fill in; the shared template is unchanged for everyone else.
                        </p>
                        <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
                          <button className="btn btn-confirm" onClick={() => setEditingThisOne(false)}>Done editing</button>
                          {bodyOverrides.has(previewGroup.email) && (
                            <button className="btn" onClick={() => {
                              setBodyOverrides((prev) => { const n = new Map(prev); n.delete(previewGroup.email); return n; });
                              setEditingThisOne(false);
                            }}>Reset to template</button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="preview-box" dangerouslySetInnerHTML={{ __html: buildBodyHighlighted(previewGroup) || '<em>(no body yet)</em>' }} />
                    )}

                    {/* Approve / edit controls */}
                    {!editingThisOne && (
                      <>
                        <p style={{ fontSize: '0.62rem', color: '#8a8a8a', margin: '10px 0 0' }}>
                          <span style={{ background: '#fdf07a', color: '#111', borderRadius: 3, padding: '0 4px' }}>Highlighted</span> = auto-filled from the show/contact
                        </p>
                        <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          className={approvedEmails.has(previewGroup.email) ? 'btn btn-toggle-on' : 'btn btn-confirm'}
                          onClick={() => {
                            const wasApproved = approvedEmails.has(previewGroup.email);
                            toggleApprove(previewGroup.email);
                            // On approve, auto-advance to the next un-approved one for a fast pass.
                            if (!wasApproved && safePreviewIdx < groupedRecipients.length - 1) {
                              setPreviewIdx((i) => Math.min(groupedRecipients.length - 1, i + 1));
                            }
                          }}
                        >
                          {approvedEmails.has(previewGroup.email) ? '✓ Approved' : 'Approve'}
                        </button>
                        <button className="btn" onClick={() => setEditingThisOne(true)}>Edit this one</button>
                        {bodyOverrides.has(previewGroup.email) && (
                          <span style={{ fontSize: '0.66rem', color: '#e1c679' }}>✎ custom copy</span>
                        )}
                        <button
                          className="btn btn-confirm"
                          style={{ marginLeft: 'auto' }}
                          onClick={handleSend}
                          disabled={sending || !allApproved}
                          title={!allApproved ? 'Approve every recipient first' : ''}
                        >
                          {sending ? (sendProgress || 'Sending…') : allApproved ? `Send ${groupedRecipients.length} email${groupedRecipients.length !== 1 ? 's' : ''}` : `${approvedEmails.size}/${groupedRecipients.length} approved`}
                        </button>
                      </div>
                      {/* Stagger toggle - for larger batches, space sends out */}
                      {groupedRecipients.length > 1 && (
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <button
                            className={staggerSend ? 'btn btn-toggle-on' : 'btn'}
                            style={{ padding: '6px 12px', fontSize: '0.62rem' }}
                            onClick={() => setStaggerSend((s) => !s)}
                            disabled={sending}
                          >
                            {staggerSend ? '✓ Spacing sends out' : 'Space sends out'}
                          </button>
                          <span style={{ fontSize: '0.64rem', color: '#8a8a8a' }}>
                            {staggerSend
                              ? `Sends one every ${STAGGER_DELAY_MS / 1000}s (~${Math.ceil((groupedRecipients.length - 1) * STAGGER_DELAY_MS / 60000)} min total) — keep this tab open. Better for big batches & deliverability.`
                              : 'Off = all at once (fast). Turn on for large batches to look human and protect your domain.'}
                          </span>
                        </div>
                      )}
                      </>
                    )}
                  </>
                ) : (
                  <p style={{ color: '#9a9a9a', fontSize: '0.85rem' }}>Select a recipient to preview.</p>
                )}
              </div>
            )}

            {/* Signature */}
            <div className="compose-row" style={{ marginTop: 4 }}>
              <button className="btn" onClick={() => setShowSignatureEditor((s) => !s)}>
                {signature.trim() ? '✓ Signature set' : 'Add signature'} {showSignatureEditor ? '▾' : '▸'}
              </button>
              <span style={{ fontSize: '0.66rem', color: '#8a8a8a' }}>Appended to the bottom of every send. Paste HTML (with your link) here.</span>
            </div>
            {showSignatureEditor && (
              <div className="compose-row" style={{ display: 'block' }}>
                <textarea
                  className="admin-textarea"
                  rows={4}
                  placeholder={'e.g.  Brian Daniels — Bdan Media\\n<a href="https://bdanmedia.com">bdanmedia.com</a>'}
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
                  <button className="btn btn-confirm" onClick={saveSignature}>Save signature</button>
                  <button className="btn" onClick={() => { setSignature(DEFAULT_SIGNATURE); localStorage.removeItem('outreach_signature'); showToast('Reset to default signature'); }}>Reset to default</button>
                  <span style={{ fontSize: '0.66rem', color: '#8a8a8a', alignSelf: 'center' }}>Defaults to your BDAN MEDIA signature automatically.</span>
                </div>
              </div>
            )}

            {/* Bottom send bar - only on the Write tab, since the Preview tab
                has its own inline Send button. Avoids two Send buttons. */}
            {composeView === 'write' && (
              <div className="compose-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.68rem', color: allApproved ? '#7ed9a0' : '#9a9a9a' }}>
                  {groupedRecipients.length === 0
                    ? ''
                    : allApproved
                      ? '✓ All recipients approved — ready to send'
                      : `Approve all ${groupedRecipients.length} in Preview to unlock Send (${approvedEmails.size}/${groupedRecipients.length} done)`}
                </span>
                <button className="btn btn-confirm" onClick={() => { if (!allApproved) { setComposeView('preview'); showToast('Review & approve each recipient first'); } else { handleSend(); } }} disabled={sending}>
                  {sending ? 'Sending…' : allApproved ? `Send ${groupedRecipients.length} email${groupedRecipients.length !== 1 ? 's' : ''}` : 'Review in Preview →'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Standalone add-contact modal */}
      {standaloneOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setStandaloneOpen(false)}
        >
          <div
            style={{ background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 6, padding: 32, width: '100%', maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.2em', color: '#4db0f2', textTransform: 'uppercase', margin: '0 0 16px' }}>Add contact to database</p>
            <label style={{ fontSize: '0.66rem', color: '#9a9a9a', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Artist / contact name</label>
            <input
              className="admin-input"
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="e.g. Oliver Heldens"
              value={standaloneName}
              onChange={(e) => setStandaloneName(e.target.value)}
              autoFocus
            />
            <label style={{ fontSize: '0.66rem', color: '#9a9a9a', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Email</label>
            <input
              className="admin-input"
              style={{ width: '100%', marginBottom: 20 }}
              placeholder="manager@example.com"
              value={standaloneEmail}
              onChange={(e) => setStandaloneEmail(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setStandaloneOpen(false)}>Cancel</button>
              <button className="btn btn-confirm" onClick={saveStandaloneContact} disabled={savingContact}>
                {savingContact ? 'Saving…' : 'Add to database'}
              </button>
            </div>
            <p style={{ fontSize: '0.66rem', color: '#8a8a8a', margin: '14px 0 0' }}>
              If the name already exists, this email is added to that contact instead of creating a duplicate. Sorted into the sheet alphabetically.
            </p>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </>
  );
};

export default OutreachPage;